import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { shopAxios } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';

/**
 * Shown after shop registration — no shop session yet.
 * Admin registration never uses this page.
 */
const CheckEmail = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const emailFromState = (location.state as { email?: string } | null)?.email ?? '';
    const [email, setEmail] = useState(emailFromState);
    const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleResend = async () => {
        if (!email.trim()) {
            setMessage(t('page.checkEmail.emailRequired'));
            setResendStatus('error');
            return;
        }
        setResendStatus('sending');
        setMessage('');
        try {
            await shopAxios.post('/auth/resend-confirmation', { email: email.trim() });
            setResendStatus('sent');
            setMessage(t('page.checkEmail.resent'));
        } catch (err: unknown) {
            setResendStatus('error');
            const status = (err as { response?: { status?: number } })?.response?.status;
            setMessage(
                status === 429
                    ? t('error.toomanyattempts')
                    : t('page.checkEmail.resendFailed')
            );
        }
    };

    return (
        <div className="mx-auto max-w-md px-4 py-10 text-center sm:py-16">
            <h1 className="mb-3 text-2xl font-bold sm:text-3xl">
                {t('page.checkEmail.title')}
            </h1>
            <p className="text-gray-600 mb-6">
                {t('page.checkEmail.body')}
            </p>
            {emailFromState && (
                <p className="mb-6 font-medium text-[var(--text-color)]">{emailFromState}</p>
            )}

            <div className="rounded-lg border border-gray-200 bg-white p-6 text-left space-y-4 shadow-sm">
                <label className="block text-sm font-medium" htmlFor="resend-email">
                    {t('page.checkEmail.resendLabel')}
                </label>
                <input
                    id="resend-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border p-3 w-full rounded-lg"
                    placeholder={t('page.register.emailPlaceholder')}
                />
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendStatus === 'sending'}
                    className="w-full rounded px-4 py-2 font-medium bg-[var(--accent-color)] text-white disabled:opacity-60"
                >
                    {resendStatus === 'sending'
                        ? t('state.loading')
                        : t('page.checkEmail.resendButton')}
                </button>
                {message && (
                    <p className={`text-sm ${resendStatus === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                        {message}
                    </p>
                )}
            </div>

            <div className="mt-8 space-y-3 text-sm text-gray-600">
                <p>{t('page.checkEmail.existingHint')}</p>
                <p>
                    {t('page.checkEmail.alreadyConfirmed')}{' '}
                    <Link to="/login" className="underline font-medium text-[var(--accent-color)]">
                        {t('page.login.title')}
                    </Link>
                </p>
                <p>
                    {t('page.checkEmail.forgotPasswordHint')}{' '}
                    <Link
                        to="/forgot-password"
                        className="underline font-medium text-[var(--accent-color)]"
                    >
                        {t('page.login.forgotPassword')}
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default CheckEmail;
